import { Fragment, useCallback, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import { InfoIcon } from "lucide-react";

import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { Button } from "simplydo/react/ui/button";
import {
  Checkbox,
  CheckboxContent,
  CheckboxHeader,
} from "simplydo/react/ui/checkbox";
import { Divider } from "simplydo/react/ui/divider";
import { FormField } from "simplydo/react/ui/form";
import { H5 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { List, ListItem } from "simplydo/react/ui/list";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

interface IChallengeStageChanger {
  challengeId: string;
  completed?:
    | {
        audienceCompleted: "done" | "almost" | false;
        templateCompleted: "done" | "almost" | false;
        detailsCompleted: "done" | "almost" | false;
        announcementCompleted: "done" | "almost" | false;
      }
    | undefined;
  announcements: OpenAPI.Schemas["Announcement"][];
  template: any;
  updateStage: (newStage: string, openWithComms?: boolean) => void;
}

const ChallengeStageChanger = ({
  challengeId,
  updateStage,
  completed: propCompleted,
  announcements,
  template,
}: IChallengeStageChanger) => {
  const user = useAppSelector((state) => state.user);
  const challengeQuery = useQuery(
    initUseQuery(api.challenges.get, [challengeId], {
      isEnabled: !!challengeId,
    }),
  );
  const challenge = challengeQuery.data;

  const [confirmationModalOpen, setConfirmationModalOpen] =
    useState<boolean>(false);
  const deadline = challenge?.deadline;
  const { t } = useTranslation();

  const [sendCommunications, setSendCommunications] = useState(false);

  const deadlineInPast = useMemo(
    () => deadline && new Date(deadline) < new Date(),
    [deadline],
  );

  const openChallenge = useCallback(() => {
    setConfirmationModalOpen(true);
  }, []);

  const closeChallenge = useCallback(() => {
    updateStage("closed");
  }, [updateStage]);

  const noAudience = useMemo(
    () => !challenge?.audienceSize || challenge?.audienceSize === 0,
    [challenge?.audienceSize],
  );

  const noIdeaFormat = useMemo(
    () =>
      !challenge?.ownerIdeaTemplate?.body ||
      challenge?.ownerIdeaTemplate.body?.length === 0,
    [challenge?.ownerIdeaTemplate?.body],
  );

  const completed = { ...(propCompleted || {}) };
  if (!propCompleted) {
    completed.audienceCompleted =
      challenge?.audienceSize && challenge?.audienceSize > 0 ? "done" : false;
    completed.templateCompleted = template?.body?.length > 0 ? "done" : false;

    const isDone = !!(
      challenge?.description &&
      challenge?.description?.length > 0 &&
      challenge?.description !== undefined &&
      challenge?.instructions &&
      challenge?.instructions?.length > 0 &&
      challenge?.instructions !== undefined &&
      challenge?.bannerImage &&
      challenge?.name
    );

    const isAlmost =
      challenge?.description ||
      challenge?.instructions ||
      challenge?.sector ||
      challenge?.video ||
      challenge?.coverImage ||
      challenge?.bannerImage;

    completed.detailsCompleted = isDone ? "done" : isAlmost ? "almost" : false;
    completed.announcementCompleted =
      announcements?.length > 0 ? "done" : false;
  }

  return (
    <Fragment>
      <Modal
        open={confirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
      >
        <ModalHeader>Open {t("generic.challenge")} to audience</ModalHeader>
        <ModalContent>
          {(completed.audienceCompleted !== "done" ||
            completed.templateCompleted !== "done" ||
            completed.announcementCompleted !== "done" ||
            completed.detailsCompleted !== "done") && (
            <>
              <Message color="orange">
                <Icon icon={InfoIcon} />
                <MessageHeader>Recommended settings incomplete</MessageHeader>
                <MessageContent>
                  {`You have not completed all the recommended sections of the ${t("generic.challenge")}. Please review the ${t("generic.challenge")} settings before opening the ${t("generic.challenge")}.`}
                </MessageContent>
              </Message>
              <Divider hidden />
            </>
          )}
          <p>
            You are about to open the {t("generic.challenge")} &quot;
            {challenge?.name}&quot; to its audience. Please read and review the
            following before proceeding:
          </p>
          {challenge?.audienceSize && challenge?.audienceSize > 0 ? (
            <H5>
              There are{" "}
              {util.pluralise(challenge.audienceSize, "user", "users")} in the
              audience of this {t("generic.challenge")}
            </H5>
          ) : null}
          <List variant="bulleted">
            <ListItem>
              Anyone in the {t("generic.challenge")} audience will be able to
              make {t("generic.ideas")} for this {t("generic.challenge")}.
            </ListItem>
            <ListItem>
              All information that is part of the {t("generic.challenge")}{" "}
              details will be visible to people viewing the{" "}
              {t("generic.challenge")}. This includes any text, images or files
              in the summary, description and attachments. Please verify no
              sensitive information will be revealed.
            </ListItem>
          </List>
          {util.hasPermission(
            user,
            "org.viewDashboard",
            challenge?.organisation,
          ) ? (
            <>
              <H5>User notifications</H5>
              <FormField>
                <Checkbox
                  type="radio"
                  name="without"
                  value="without"
                  checked={!sendCommunications}
                  onChange={() => setSendCommunications(false)}
                >
                  <CheckboxHeader>
                    Open the {t("generic.challenge")} to the audience, but do
                    not send notification emails
                  </CheckboxHeader>
                  <CheckboxContent>
                    Users will be able to view and access the{" "}
                    {t("generic.challenge")}, but they will not receive any
                    notification prompting them to view the{" "}
                    {t("generic.challenge")}.
                  </CheckboxContent>
                </Checkbox>
              </FormField>
              <FormField>
                <Checkbox
                  type="radio"
                  name="with"
                  value="with"
                  checked={sendCommunications}
                  onChange={() => setSendCommunications(true)}
                >
                  <CheckboxHeader>
                    Open the {t("generic.challenge")} to the audience and send
                    notification emails
                  </CheckboxHeader>
                  <CheckboxContent>
                    Emails and notifications will be sent to users with tags
                    that match this {t("generic.challenge")}. The email will
                    prompt them to view and participate in the{" "}
                    {t("generic.challenge")}.
                  </CheckboxContent>
                </Checkbox>
              </FormField>
            </>
          ) : null}
          {noIdeaFormat || noAudience ? (
            <Message color="orange">
              <MessageHeader style={{ marginBottom: 5 }}>
                Missing {t("generic.challenge")} information
              </MessageHeader>
              <MessageContent>
                There seems to be some missing information in the{" "}
                {t("generic.challenge")} settings. You can open the{" "}
                {t("generic.challenge")} now, but please be aware that this may
                cause issues for users. Make sure to review this information
                before opening the {t("generic.challenge")}. The following
                issues have been identified:
                <List variant="bulleted" style={{ marginTop: 10 }}>
                  {noAudience ? (
                    <ListItem>
                      <b>
                        There is currently no audience set up for this{" "}
                        {t("generic.challenge")}.
                      </b>
                    </ListItem>
                  ) : null}
                  {noIdeaFormat ? (
                    <ListItem>
                      <b>
                        There is currently no {t("generic.idea")} format
                        selected for this {t("generic.challenge")}.
                      </b>
                    </ListItem>
                  ) : null}
                </List>
                <p>
                  Please revisit the settings to finish setting up this{" "}
                  {t("generic.challenge")}.
                </p>
              </MessageContent>
            </Message>
          ) : null}
        </ModalContent>
        <ModalActions>
          <Button
            onClick={() => {
              setSendCommunications(false);
              setConfirmationModalOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmationModalOpen(false);
              setSendCommunications(false);
              updateStage("published", sendCommunications);
            }}
            color="primary"
          >{`Open ${t("generic.challenge")}`}</Button>
        </ModalActions>
      </Modal>
      {challenge?.stage === "published" ? (
        <Button size="small" onClick={closeChallenge} color="secondary">
          {t("challenge.stages.published.close")}
        </Button>
      ) : !challenge?.stage ? (
        <Button size="small" onClick={openChallenge} color="primary">
          {t("challenge.stages.draft.open")}
        </Button>
      ) : challenge?.stage === "closed" ? (
        <Button
          size="small"
          tooltip={
            deadlineInPast
              ? `You cannot open this ${t("generic.challenge")} as it currently has a deadline in the past, and therefore will close as soon as it is opened. Please update the deadline, or remove it, in the ${t("generic.challenge")} settings`
              : null
          }
          disabled={!!deadlineInPast}
          onClick={() => openChallenge()}
          color="primary"
        >
          {t("challenge.stages.closed.reOpen")}
        </Button>
      ) : null}
    </Fragment>
  );
};

export default ChallengeStageChanger;
